import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SnapshotListItem } from '../../types/snapshot.types';
import { useCompanyContextReady } from '../company/use-company-context-ready.hook';

const SNAPSHOTS_STALE_TIME_MS = 5 * 60 * 1000;

export type FetchCompanies = {
  capTableMode?: boolean;
  companyId: string;
  currentPage?: number;
  enabled?: boolean;
  take?: number;
};

// Slim list rows: scalar figures + valuation/incorporation + per-stakeholder {id, name, issued}.
// Full stats for a selected row come from useSnapshot (by-id fetch).
type GetCompaniesResponseType = {
  data: SnapshotListItem[];
  totalPages: number;
};

/** Keep `/snapshots` and `/cap-table-snapshots` on distinct React Query cache entries. */
export const buildSnapshotsQueryKey = ({
  companyId,
  currentPage = 1,
  take = 100,
  capTableMode,
}: Pick<FetchCompanies, 'capTableMode' | 'companyId' | 'currentPage' | 'take'>) => {
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;
  return [
    QueryKey.GET_SNAPSHOTS,
    {
      companyId,
      currentPage,
      take,
      skip,
      capTableMode: Boolean(capTableMode),
    },
  ] as const;
};

export const useSnapshots = ({
  currentPage = 1,
  take = 100,
  companyId,
  capTableMode,
  enabled = true,
}: FetchCompanies) => {
  const queryClient = useQueryClient();
  const isCompanyContextReady = useCompanyContextReady(companyId);
  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery<GetCompaniesResponseType>({
      // capTableMode must be part of the key: `/snapshots` and `/cap-table-snapshots` hit
      // different tables. Incorporation rows exist only on the cap-table variant — a shared key
      // let a non-cap-table fetch overwrite the list so re-selecting Incorporation resolved to
      // nothing and the page showed an empty state.
      queryKey: buildSnapshotsQueryKey({ companyId, currentPage, take, capTableMode }),
      queryFn: ({ pageParam = 0 }) => {
        const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;
        return ApiService.get(
          {
            endpoint: BackendRoute.COMPANIES,
            routePath: [companyId, capTableMode ? 'cap-table-snapshots' : 'snapshots'],
          },
          {
            queryParams: {
              take,
              skip: currentPage ? skip : pageParam,
            },
          },
        );
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.totalPages > allPages.length ? allPages.length * take : undefined,

      enabled: !!companyId && enabled && isCompanyContextReady,
      // Each page carries full snapshots (multi-MB on a heavy company), and the QueryClient has no
      // global staleTime — so without this the list refetched on every mount and every window
      // focus. Event/safe mutations invalidate QueryKey.GET_SNAPSHOTS, so freshness does not rely
      // on this window.
      staleTime: SNAPSHOTS_STALE_TIME_MS,
    });

  const snapshotList = useMemo(
    () =>
      data?.pages?.reduce<SnapshotListItem[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []),
    [data?.pages],
  );

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SNAPSHOTS] });
  };

  return {
    invalidateQuery,
    snapshots: snapshotList || [],
    totalPages: data?.pages[0]?.totalPages || 0,
    isEmpty: snapshotList?.length === 0,
    fetchNextPage,
    hasNextPage,
    isLoading,
  };
};
